// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { Facility } from '../types';
import { scatterLogLog, divergingBars, type ScatterPoint } from '../components/charts';
import { attachSvgZoom } from '../utils/svgZoom';
import { bySector, rankedBy } from '../analysis';
import { OVER, UNDER, sectorColour } from '../sectors';
import { escapeHtml, formatCompact, formatNumber } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(2)} Mt`;
const KEY = 'sg.overline.sector';

export function renderOverline(root: HTMLElement, ctx: ViewContext): void {
  const { facilities } = ctx.data;
  const sectorGroups = bySector(facilities).filter((s) => s.count >= 3);
  let sector = localStorage.getItem(KEY) || 'all';
  if (sector !== 'all' && !sectorGroups.some((s) => s.key === sector)) sector = 'all';

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Over the line</h1>
      <p class="view-sub">
        Every facility plotted by its ${glossaryTerm('baseline', 'baseline')} (across) against what it actually emitted
        (up), both on a log scale. The dashed diagonal is the baseline itself: a dot <strong>above</strong> the line
        emitted more than its cap, a dot <strong>below</strong> came in under. Being big is not the same as being over —
        the two split apart here. Dot size is emissions; colour is industry. Scroll to zoom, drag to pan, click a dot.
      </p>
    </div>

    <div class="controls">
      <span class="field-label">Industry</span>
      <select data-role="sector">
        <option value="all">All industries</option>
        ${sectorGroups.map((s) => `<option value="${escapeHtml(s.key)}"${s.key === sector ? ' selected' : ''}>${escapeHtml(s.label)} (${s.count})</option>`).join('')}
      </select>
      <span class="field-label" data-role="count" style="margin-left:auto"></span>
    </div>

    <div class="panel">
      <div class="chart-frame" data-role="scatter"></div>
      <div class="legend" data-role="legend" style="margin-top:var(--space-md)"></div>
      <p class="zoom-hint">Scroll to zoom about the cursor · drag to pan · click a dot to open the facility.</p>
    </div>

    <div class="grid-2 wide" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-title" style="color:${OVER}">Furthest over the line</div>
        <div class="panel-sub">Facilities that emitted the most above their baseline (tonnes CO₂-e over the cap). Click to open.</div>
        <div class="chart-scroll" data-role="over"></div>
      </div>
      <div class="panel">
        <div class="panel-title" style="color:${UNDER}">Furthest under the line</div>
        <div class="panel-sub">Facilities that came in the most below baseline — the biggest earners of ${glossaryTerm('smc', 'Safeguard credits')}. Click to open.</div>
        <div class="chart-scroll" data-role="under"></div>
      </div>
    </div>
  `;

  const scatterSlot = root.querySelector('[data-role="scatter"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;
  const overSlot = root.querySelector('[data-role="over"]') as HTMLElement;
  const underSlot = root.querySelector('[data-role="under"]') as HTMLElement;
  const legend = root.querySelector('[data-role="legend"]') as HTMLElement;
  let zoom: ReturnType<typeof attachSvgZoom> | null = null;

  const paint = () => {
    const subset = sector === 'all' ? facilities : facilities.filter((f) => f.sector === (sectorGroups.find((s) => s.key === sector)?.label ?? ''));
    const plotted = subset.filter((f) => f.covered > 0 && f.baseline > 0);
    countEl.textContent = `${formatNumber(plotted.length)} facilities · ${plotted.filter((f) => f.excess > 0).length} over the line`;

    const maxCov = Math.max(...plotted.map((f) => f.covered), 1);
    const rOf = (f: Facility) => 4 + Math.sqrt(f.covered / maxCov) * 16;
    const points: ScatterPoint[] = plotted.map((f) => ({
      id: f.slug,
      x: f.baseline,
      y: f.covered,
      r: rOf(f),
      colour: sectorColour(f.sector),
      tip: `${f.name}\n${f.company}\nEmitted ${formatCompact(f.covered)} · baseline ${formatCompact(f.baseline)}\n${f.excess > 0 ? `+${formatCompact(f.excess)} over the line` : `${formatCompact(f.excess)} under`}`,
    }));

    scatterSlot.innerHTML = '';
    const svg = scatterLogLog(points, {
      width: 900,
      height: 600,
      xTitle: 'Baseline (tonnes CO₂-e, log scale)',
      yTitle: 'Emitted (tonnes CO₂-e, log scale)',
      onClick: (id) => ctx.openFacility(id),
    });
    scatterSlot.appendChild(svg);
    zoom?.destroy();
    zoom = attachSvgZoom(svg, { maxScale: 12 });

    // Legend — the sectors present, biggest first.
    const present = bySector(plotted).slice(0, 8);
    legend.innerHTML =
      present
        .map((s) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch round" style="background:${sectorColour(s.label)}"></span>${escapeHtml(s.label)}</span>`)
        .join('') +
      `<span class="legend-item" style="cursor:default;margin-left:auto"><span style="display:inline-block;width:22px;border-top:2px dashed var(--text-secondary);height:0"></span>&nbsp;at baseline (dot on the line)</span>`;

    // Diverging bars.
    const over = rankedBy(plotted, 'excess', 'desc').filter((f) => f.excess > 0).slice(0, 12);
    const under = rankedBy(plotted, 'excess', 'asc').filter((f) => f.excess < 0).slice(0, 12);
    overSlot.innerHTML = '';
    underSlot.innerHTML = '';
    overSlot.appendChild(
      divergingBars(
        over.map((f) => ({ id: f.slug, label: f.name.length > 24 ? f.name.slice(0, 23) + '…' : f.name, value: f.excess, colourPos: OVER, colourNeg: UNDER, tip: `${f.name}\n${f.company}\n+${formatCompact(f.excess)} over baseline (${mt(f.covered)} vs ${mt(f.baseline)})` })),
        { width: 560, labelWidth: 190, valueFormat: (v) => formatCompact(v), onClick: (id) => ctx.openFacility(id) },
      ),
    );
    underSlot.appendChild(
      divergingBars(
        under.map((f) => ({ id: f.slug, label: f.name.length > 24 ? f.name.slice(0, 23) + '…' : f.name, value: f.excess, colourPos: OVER, colourNeg: UNDER, tip: `${f.name}\n${f.company}\n${formatCompact(f.excess)} under baseline (${mt(f.covered)} vs ${mt(f.baseline)})` })),
        { width: 560, labelWidth: 190, valueFormat: (v) => formatCompact(v), onClick: (id) => ctx.openFacility(id) },
      ),
    );
  };

  root.querySelector('[data-role="sector"]')?.addEventListener('change', (e) => {
    sector = (e.target as HTMLSelectElement).value;
    localStorage.setItem(KEY, sector);
    paint();
  });

  paint();
}
